import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPutFetcher } from '@/common/api/fetcher'
import { UpdateSiteResponse, UpdateSiteVars } from '@/common/api/params/site'
import { ApiPath } from '@/common/api/path'
import { registerNumberOption } from '@/common/form/register'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Loading from '@/components/view/atoms/icon/Loading'
import Text from '@/components/view/atoms/text/Text'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import ButtonList from '@/components/view/layouts/button/ButtonList'
import FormRow from '@/components/view/layouts/form/FormRow'

import {
  SamplingRateSchemaType,
  convertSamplingRateSchemaToVars,
  samplingRateSchema
} from '@/components/container/form/modules/site/samplingRate/samplingRateSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'

interface Props {
  siteId: number
  currentSamplingRate: number
  onComplete(samplingRate?: number): void
}

const SamplingRateForm = ({ siteId, currentSamplingRate, onComplete }: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
    control
  } = useForm<SamplingRateSchemaType>({
    resolver: zodResolver(samplingRateSchema)
  })
  const [loading, setLoading] = useState<boolean>(false)

  const { trigger } = useCallMutation<UpdateSiteResponse, UpdateSiteVars>(
    ApiPath.UPDATE_SITE,
    {
      onSuccess: (response: UpdateSiteResponse) => {
        onComplete(getValues('samplingRate'))
      }
    },
    apiPutFetcher
  )

  useEffect(() => {
    reset({ samplingRate: currentSamplingRate })
  }, [currentSamplingRate])

  const onSubmit = () => {
    setLoading(true)
    trigger(convertSamplingRateSchemaToVars(siteId, getValues()))
  }

  return (
    <FormRow direction='vertical' gap={16}>
      <FormLabel>レコメンド配信率</FormLabel>
      <FormRow gap={4} direction='vertical'>
        <FormRow align='center' gap={4}>
          <Text size={12}>現在の</Text>
          <Badge badgeType={BadgeType.SQUARE_TERTIARY}>配信率</Badge>
          <Text size={12}> {currentSamplingRate}%を</Text>
        </FormRow>
        <FormRow align='center' gap={4}>
          <FormTextField
            type={FormTextFieldType.NUMBER}
            register={register(`samplingRate`, registerNumberOption)}
            size={TextBoxSize.X_SMALL}
          />
          <Text size={12}>%に変更する</Text>
        </FormRow>
      </FormRow>
      <FormError message={errors.samplingRate?.message} />
      {loading ? (
        <Loading />
      ) : (
        <ButtonList justify='start'>
          <Button color={'secondary'} onClick={() => onComplete()}>
            キャンセル
          </Button>
          <Button type='button' onClick={onSubmit}>
            適用
          </Button>
        </ButtonList>
      )}
    </FormRow>
  )
}

export default SamplingRateForm
